import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { markActivityNow } from '../Auth/session';
import {
  Box,
  Button,
  Flex,
  Text,
  Input,
  Progress,
  Icon,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  HStack,
  Badge,
  Divider,
  Select,
} from '@chakra-ui/react';
import { CloseIcon, DownloadIcon, ExternalLinkIcon, InfoIcon, RepeatIcon } from '@chakra-ui/icons';
import { useToast } from '@chakra-ui/react';
import Chatbot from '../../components/ChatBot/Chatbot';
import { StorageProviderSelector, StorageProvider } from '../../components/upload/StorageProviderSelector';
import { CloudStorageURLInput } from '../../components/upload/CloudStorageURLInput';
import { StorageProviderInstructions } from '../../components/upload/StorageProviderInstructions';
import { FiHardDrive, FiFolder } from 'react-icons/fi';
import { BACKEND_API } from '../../constants/backendApiEndpoints';
import FeedbackModal from '../../components/common/FeedbackModal';
import { useFeedback } from '../../hooks/useFeedback';

// ID validation
const ID_RE = /^[A-Z0-9_-]{3,32}$/;
const normalizeId = (v: string) => v.replace(/\s+/g, '').toUpperCase();

// Detect file type from filename
const getFileTypeFromName = (filename: string): string => {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.vcf.gz')) return 'VCF.GZ';
  if (lower.endsWith('.vcf')) return 'VCF';
  return 'Unknown';
};

// Job status types
type JobStatus =
  | 'queued'
  | 'pending'
  | 'running'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled';

const progressMap: Record<JobStatus, number> = {
  queued: 5,
  pending: 20,
  running: 50,
  processing: 80,
  completed: 100,
  failed: 100,
  cancelled: 100,
};

const statusMessages: Record<JobStatus, string> = {
  queued: 'Queued for processing...',
  pending: 'Initializing VCF2TXT pipeline...',
  running: 'Running VEP annotation and conversion...',
  processing: 'Generating output files...',
  completed: 'Conversion complete!',
  failed: 'Conversion failed',
  cancelled: 'Conversion cancelled',
};

const VCF2TXT = () => {
  // Refs
  const vcfInputRef = useRef<HTMLInputElement>(null);
  const sampleInputRef = useRef<HTMLInputElement>(null);

  // Step 1: ID and genome inputs
  const [localSampleId, setLocalSampleId] = useState('');
  const [referenceGenome, setReferenceGenome] = useState('hg38');

  // Step 2: File upload
  const [, setVcfFile] = useState<File | null>(null);
  const [vcfFileName, setVcfFileName] = useState<string | null>(null);
  const [vcfFileId, setVcfFileId] = useState<number | null>(null);
  const [storageProvider, setStorageProvider] = useState<StorageProvider>('local');
  const [cloudURL, setCloudURL] = useState('');

  // Upload UUID for tracking
  const uploadUuidRef = useRef<string | null>(null);

  const getOrCreateUploadUuid = (): string => {
    if (!uploadUuidRef.current) {
      uploadUuidRef.current = crypto.randomUUID();
    }
    return uploadUuidRef.current;
  };

  // Job state
  const [jobId, setJobId] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [statusDetail, setStatusDetail] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isCloudTransfer, setIsCloudTransfer] = useState(false);

  // Results state
  const [resultFiles, setResultFiles] = useState<Record<string, string>>({});

  // Previous files state
  interface PreviousFile {
    key: string;
    filename: string;
    relative_path: string;
    pipeline_folder: string;
    file_type: 'vcf';
    size: number;
    last_modified: string | null;
  }
  const [previousFiles, setPreviousFiles] = useState<PreviousFile[]>([]);
  const [isLoadingPreviousFiles, setIsLoadingPreviousFiles] = useState(false);
  const { isOpen: isPreviousFilesOpen, onOpen: onOpenPreviousFiles, onClose: onClosePreviousFiles } = useDisclosure();

  // Feedback modal
  const { showFeedbackModal, setShowFeedbackModal } = useFeedback({
    jobId,
    pipelineType: 'VCF2TXT',
    jobStatus: isComplete ? 'completed' : statusDetail,
    delayMs: 5000,
  });

  // Computed validation
  const normalizedSampleId = normalizeId(localSampleId);
  const sidOk = !!normalizedSampleId && ID_RE.test(normalizedSampleId);
  const idsValid = sidOk;

  // S3 upload constants
  const PART_SIZE = 50 * 1024 * 1024;
  const CONCURRENT_UPLOADS = 4;
  const MULTIPART_THRESHOLD = 100 * 1024 * 1024;

  const toast = useToast();

  const showToast = useCallback((
    title: string,
    status: 'info' | 'warning' | 'success' | 'error'
  ) =>
    toast({ title, status, duration: 5000, isClosable: true, position: 'top' }), [toast]);

  // Caret-preserving input handler
  const handleTextChange =
    (ref: React.RefObject<HTMLInputElement>, setValue: (v: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, selectionStart } = e.target;
      setValue(value);
      requestAnimationFrame(() => {
        if (ref.current) {
          ref.current.focus();
          const pos = selectionStart ?? value.length;
          try {
            ref.current.setSelectionRange(pos, pos);
          } catch {}
        }
      });
    };

  // Fetch job results
  const fetchJobResults = useCallback(async (id: number, retries: number = 3): Promise<boolean> => {
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(BACKEND_API.VCF2TXT.RESULTS(id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to fetch results:', errorData);
        if (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          return fetchJobResults(id, retries - 1);
        }
        showToast(`Failed to fetch results: ${errorData.detail || 'Unknown error'}`, 'error');
        return false;
      }
      
      const data = await res.json();
      setResultFiles(data.files || {});
      return true;
    } catch (err) {
      console.error('Failed to fetch results:', err);
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        return fetchJobResults(id, retries - 1);
      }
      showToast('Network error while fetching results. Please try again.', 'error');
      return false;
    }
  }, [showToast]);

  // URL search params for direct navigation
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const urlJobId = searchParams.get('jobId');
    const urlView = searchParams.get('view');
    if (urlJobId && urlView === 'completed') {
      const parsedJobId = parseInt(urlJobId, 10);
      if (!isNaN(parsedJobId)) {
        setJobId(parsedJobId);
        setIsComplete(true);
        setProgress(100);
        fetchJobResults(parsedJobId);
      }
    }
  }, [searchParams, fetchJobResults]);

  // Job status polling
  useEffect(() => {
    if (!jobId) return;
    if (isComplete) return;
    
    const pollStatus = async () => {
      const token = localStorage.getItem('access_token');
      try {
        const res = await fetch(BACKEND_API.VCF2TXT.STATUS(jobId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          console.error('Status poll failed:', res.status);
          return;
        }
        const data = await res.json();
        const status = data.status as JobStatus;
        
        setStatusDetail(statusMessages[status] || status);
        setProgress(data.progress || progressMap[status] || 0);
        
        if (status === 'completed') {
          setIsComplete(true);
          setIsAnalyzing(false);
          fetchJobResults(jobId);
          return true;
        } else if (status === 'failed' || status === 'cancelled') {
          setIsAnalyzing(false);
          showToast(`Conversion ${status}`, status === 'failed' ? 'error' : 'warning');
          return true;
        }
        return false;
      } catch (err) {
        console.error('Status poll error:', err);
        return false;
      }
    };
    
    pollStatus();
    
    const pollInterval = setInterval(async () => {
      const shouldStop = await pollStatus();
      if (shouldStop) {
        clearInterval(pollInterval);
      }
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [jobId, isComplete, fetchJobResults, showToast]);

  // S3 upload helpers
  const getS3PresignedUrl = async (filename: string, contentType: string = 'application/octet-stream', existingUuid?: string | null) => {
    const token = localStorage.getItem('access_token');
    const params = new URLSearchParams({ filename, content_type: contentType });
    if (existingUuid) params.append('upload_uuid', existingUuid);
    const res = await fetch(`${BACKEND_API.S3_VCF2TXT.PRESIGN}?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to get presigned URL');
    return await res.json();
  };

  const initiateS3MultipartUpload = async (filename: string, contentType: string = 'application/octet-stream', existingUuid?: string | null) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(BACKEND_API.S3_VCF2TXT.MULTIPART_INITIATE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ filename, content_type: contentType, upload_uuid: existingUuid || undefined }),
    });
    if (!res.ok) throw new Error('Failed to initiate multipart upload');
    return await res.json();
  };

  const getS3PartPresignedUrl = async (key: string, uploadId: string, partNumber: number) => {
    const token = localStorage.getItem('access_token');
    const params = new URLSearchParams({ key, upload_id: uploadId, part_number: partNumber.toString() });
    const res = await fetch(`${BACKEND_API.S3_VCF2TXT.MULTIPART_PRESIGN_PART}?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Failed to get presigned URL for part ${partNumber}`);
    return await res.json();
  };

  const completeS3MultipartUpload = async (uploadId: string, key: string, parts: { PartNumber: number; ETag: string }[], fileType: string) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(BACKEND_API.S3_VCF2TXT.MULTIPART_COMPLETE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ upload_id: uploadId, key, parts, file_type: fileType }),
    });
    if (!res.ok) throw new Error('Failed to complete multipart upload');
    return await res.json();
  };

  const confirmS3Upload = async (key: string, filename: string, fileType: string) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(BACKEND_API.S3_VCF2TXT.CONFIRM_UPLOAD, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ key, filename, file_type: fileType }),
    });
    if (!res.ok) throw new Error('Failed to confirm upload');
    return await res.json();
  };

  const uploadToS3Single = async (file: File, presignedUrl: string, contentType: string, setProgressFn: (p: number) => void) => {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', contentType);
      
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setProgressFn(Math.round((e.loaded / e.total) * 100));
        }
      };
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      };
      
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(file);
    });
  };

  const uploadToS3Multipart = async (file: File, uploadId: string, key: string, setProgressFn: (p: number) => void) => {
    const numParts = Math.ceil(file.size / PART_SIZE);
    const parts: { PartNumber: number; ETag: string }[] = new Array(numParts);
    let uploadedBytes = 0;

    const uploadPart = async (partNumber: number): Promise<void> => {
      const start = (partNumber - 1) * PART_SIZE;
      const end = Math.min(start + PART_SIZE, file.size);
      const chunk = file.slice(start, end);

      const { upload_url } = await getS3PartPresignedUrl(key, uploadId, partNumber);

      const etag = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload_url, true);
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const etag = xhr.getResponseHeader('ETag');
            if (etag) resolve(etag.replace(/"/g, ''));
            else reject(new Error('No ETag in response'));
          } else {
            reject(new Error(`Part upload failed: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(chunk);
      });

      parts[partNumber - 1] = { PartNumber: partNumber, ETag: etag };
      uploadedBytes += (end - start);
      setProgressFn(Math.round((uploadedBytes / file.size) * 100));
      markActivityNow(); // Keep session alive during long uploads
    };

    for (let i = 0; i < numParts; i += CONCURRENT_UPLOADS) {
      const batch = [];
      for (let j = i; j < Math.min(i + CONCURRENT_UPLOADS, numParts); j++) {
        batch.push(uploadPart(j + 1));
      }
      await Promise.all(batch);
    }

    return parts;
  };

  // Local file upload handler
  const handleLocalFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!idsValid) {
      showToast('Please enter valid Sample ID before uploading.', 'warning');
      return;
    }

    setVcfFile(file);
    setIsUploading(true);
    setIsCloudTransfer(false);
    setUploadProgress(0);

    try {
      const contentType = 'application/octet-stream';
      const fileType = getFileTypeFromName(file.name);
      const existingUuid = getOrCreateUploadUuid();
      
      if (file.size > MULTIPART_THRESHOLD) {
        const { upload_id, key } = await initiateS3MultipartUpload(file.name, contentType, existingUuid);
        const parts = await uploadToS3Multipart(file, upload_id, key, setUploadProgress);
        const result = await completeS3MultipartUpload(upload_id, key, parts, fileType);
        
        setVcfFileName(file.name);
        setVcfFileId(result.file_id);
        showToast('VCF file uploaded successfully!', 'success');
      } else {
        const { upload_url, key } = await getS3PresignedUrl(file.name, contentType, existingUuid);
        await uploadToS3Single(file, upload_url, contentType, setUploadProgress);
        const result = await confirmS3Upload(key, file.name, fileType);
        
        setVcfFileName(file.name);
        setVcfFileId(result.file_id);
        showToast('VCF file uploaded successfully!', 'success');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      showToast(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Cloud URL submission
  const handleCloudURLSubmit = async () => {
    if (!cloudURL.trim() || !idsValid) {
      showToast('Please enter a valid URL and Sample ID', 'warning');
      return;
    }

    const urlFileName = cloudURL.split('/').pop()?.split('?')[0] || 'cloud_file.vcf.gz';
    const fileType = getFileTypeFromName(urlFileName);
    const existingUuid = getOrCreateUploadUuid();

    setIsUploading(true);
    setIsCloudTransfer(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('access_token');
      
      const res = await fetch(BACKEND_API.S3_VCF2TXT.TRANSFER_FROM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          source_url: cloudURL,
          filename: urlFileName,
          upload_uuid: existingUuid,
          file_type: fileType,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ detail: 'Transfer failed' }));
        throw new Error(errorData.detail || 'Transfer failed');
      }

      const { transfer_id } = await res.json();
      
      let completed = false;
      while (!completed) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        const statusRes = await fetch(BACKEND_API.S3_VCF2TXT.TRANSFER_STATUS(transfer_id), {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!statusRes.ok) throw new Error('Failed to get transfer status');
        
        const status = await statusRes.json();
        setUploadProgress(status.progress || 0);
        
        if (status.status === 'completed') {
          completed = true;
          setVcfFileName(urlFileName);
          setVcfFileId(status.file_id);
          showToast('VCF file transferred successfully!', 'success');
        } else if (status.status === 'failed') {
          throw new Error(status.error || 'Transfer failed');
        }
      }
      
    } catch (err) {
      console.error('Cloud transfer failed:', err);
      showToast(`Transfer failed: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    } finally {
      setIsUploading(false);
      setIsCloudTransfer(false);
      setUploadProgress(0);
    }
  };

  // Clear file upload
  const clearFileUpload = () => {
    setVcfFile(null);
    setVcfFileName(null);
    setVcfFileId(null);
    setCloudURL('');
  };

  // Start conversion
  const handleConversion = async () => {
    const hasVcfFromUpload = !!vcfFileId;
    const hasVcfFromPrevious = cloudURL.startsWith('s3://');
    
    if (!hasVcfFromUpload && !hasVcfFromPrevious) {
      showToast('Please upload or select a VCF file.', 'warning');
      return;
    }
    if (!sidOk) {
      showToast('Please enter a valid Sample ID.', 'warning');
      return;
    }
    
    const isUsingPreviousFiles = hasVcfFromPrevious && !hasVcfFromUpload;
    
    if (!isUsingPreviousFiles && !uploadUuidRef.current) {
      showToast('Upload not completed. Please re-upload the file.', 'error');
      return;
    }

    setIsAnalyzing(true);
    setProgress(5);
    setStatusDetail('Starting conversion...');

    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      
      if (isUsingPreviousFiles) {
        formData.append('vcf_s3_path', cloudURL);
        const newUuid = crypto.randomUUID();
        formData.append('upload_uuid', newUuid);
        uploadUuidRef.current = newUuid;
      } else {
        formData.append('upload_uuid', uploadUuidRef.current!);
      }
      
      formData.append('sample_id', normalizedSampleId);
      formData.append('reference_genome', referenceGenome);

      const res = await fetch(BACKEND_API.VCF2TXT.RUN, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Request failed: ${res.status}`);
      }

      const data = await res.json();
      setJobId(data.job_id);
      showToast('Conversion started successfully!', 'success');
    } catch (err) {
      console.error('Failed to start conversion:', err);
      showToast(`Failed to start conversion: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
      setIsAnalyzing(false);
      setProgress(0);
      setStatusDetail('');
    }
  };

  // Reset functions
  const resetJobState = () => {
    setProgress(0);
    setIsComplete(false);
    setJobId(null);
    setStatusDetail('');
    setIsAnalyzing(false);
    setResultFiles({});
  };

  const resetToStart = () => {
    setVcfFile(null);
    setVcfFileName(null);
    setVcfFileId(null);
    setCloudURL('');
    setProgress(0);
    setIsComplete(false);
    setJobId(null);
    setStatusDetail('');
    setLocalSampleId('');
    setReferenceGenome('hg38');
    setIsAnalyzing(false);
    setResultFiles({});
    uploadUuidRef.current = null;
  };

  // Cancel job
  const handleCancelJob = async () => {
    if (!jobId) return;
    setIsCancelling(true);
    
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(BACKEND_API.VCF2TXT.CANCEL(jobId), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Cancel failed: ${res.status}`);
      }

      showToast('Job cancelled. You can re-run the conversion.', 'success');
      resetJobState();
    } catch (err) {
      console.error('Failed to cancel job:', err);
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  // Fetch previous files from S3
  const fetchPreviousFiles = async () => {
    setIsLoadingPreviousFiles(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(BACKEND_API.VCF2TXT.PREVIOUS_FILES, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch previous files');
      }

      const data = await res.json();
      setPreviousFiles(data.files || []);
    } catch (err) {
      console.error('Error fetching previous files:', err);
      showToast('Failed to load previous files', 'error');
    } finally {
      setIsLoadingPreviousFiles(false);
    }
  };

  // Open previous files modal
  const openSelectPrevious = () => {
    fetchPreviousFiles();
    onOpenPreviousFiles();
  };

  // Select a previous file
  const selectPreviousFile = (file: PreviousFile) => {
    setVcfFileName(file.filename);
    setCloudURL(`s3://seq4life-storage/${file.key}`);
    setStorageProvider('previous');
    setVcfFileId(null);

    showToast(`Selected: ${file.filename}`, 'success');
    onClosePreviousFiles();
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // ==================== UI COMPONENTS ====================

  const InputsSection = () => (
    <Flex mt={8} direction="column" maxW="400px" mx="auto" gap={6}>
      <Box>
        <Text mb={1} fontWeight="medium" fontSize="lg" textAlign="left">
          Sample ID*
        </Text>
        <Input
          placeholder="Enter the sample name or ID"
          value={localSampleId}
          onChange={handleTextChange(sampleInputRef, setLocalSampleId)}
          ref={sampleInputRef}
          maxLength={32}
          borderColor={localSampleId && !sidOk ? 'red.400' : 'surface.300'}
          focusBorderColor="primary.500"
          autoComplete="off"
          spellCheck={false}
          size="lg"
          bg="white"
          borderRadius="md"
          _hover={{ bg: 'surface.100' }}
          _focus={{ bg: 'white', borderColor: 'primary.500', boxShadow: 'none' }}
        />
        {localSampleId && !sidOk && (
          <Text mt={1} fontSize="sm" color="red.500" textAlign="left">
            3–32 chars: A–Z, 0–9, _ or -
          </Text>
        )}
      </Box>
      <Box>
        <Text mb={1} fontWeight="medium" fontSize="lg" textAlign="left">
          Reference Genome*
        </Text>
        <Select
          value={referenceGenome}
          onChange={(e) => setReferenceGenome(e.target.value)}
          size="lg"
          bg="white"
          borderColor="surface.300"
          focusBorderColor="primary.500"
          borderRadius="md"
          _hover={{ bg: 'surface.100' }}
        >
          <option value="hg38">GRCh38 / hg38</option>
          <option value="hg19">GRCh37 / hg19</option>
        </Select>
      </Box>
    </Flex>
  );

  const FileUploadSection = () => {
    const isFromPrevious = cloudURL.startsWith('s3://') && !vcfFileId;

    return (
      <Box mt={6} p={6} border="2px solid rgba(0,0,0,0.06)" borderRadius="2xl" bg="white" w="100%" maxW="700px" mx="auto">
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="lg" fontWeight="semibold" color="primary.500">
            VCF File
          </Text>
          {vcfFileName && (
            <Button size="sm" variant="ghost" colorScheme="red" leftIcon={<CloseIcon boxSize={3} />} onClick={clearFileUpload}>
              Remove
            </Button>
          )}
        </Flex>

        {vcfFileName ? (
          <Flex align="center" justify="space-between" p={4} bg="surface.100" borderRadius="xl">
            <Text fontWeight="medium" color="primary.500" isTruncated maxW="400px">{vcfFileName}</Text>
            <Badge colorScheme={isFromPrevious ? 'blue' : 'green'} fontSize="sm" px={2} py={1}>
              {isFromPrevious ? 'From Previous' : 'Uploaded'}
            </Badge>
          </Flex>
        ) : (
          <>
            <StorageProviderSelector 
              selectedProvider={storageProvider} 
              onChange={(provider) => {
                if (!idsValid && provider !== 'local') {
                  showToast('Please enter a valid Sample ID first (3-32 characters: A-Z, 0-9, _ or -)', 'warning');
                  return;
                }
                setStorageProvider(provider);
                if (provider === 'previous') {
                  openSelectPrevious();
                }
              }} 
              showPrevious={true} 
            />

            {storageProvider === 'local' && (
              <Flex mt={4} justify="center" align="center" gap={4} wrap="wrap">
                <Input
                  type="file"
                  ref={vcfInputRef}
                  display="none"
                  accept=".vcf,.vcf.gz,application/gzip,application/x-gzip"
                  onChange={handleLocalFileUpload}
                  disabled={!idsValid}
                />
                <Button
                  leftIcon={<Icon as={FiHardDrive} />}
                  bg="primary.500"
                  color="white"
                  fontWeight="bold"
                  px={8}
                  py={6}
                  borderRadius="full"
                  onClick={() => vcfInputRef.current?.click()}
                  isDisabled={!idsValid || isUploading}
                  isLoading={isUploading}
                  loadingText="Uploading..."
                  _hover={{ bg: 'secondary.500', transform: 'scale(1.03)' }}
                  _active={{ bg: 'primary.500', transform: 'scale(0.99)' }}
                  transition="all 0.18s ease-in-out"
                >
                  Upload VCF File
                </Button>
              </Flex>
            )}

            {storageProvider !== 'local' && storageProvider !== 'previous' && (
              <>
                <CloudStorageURLInput
                  provider={storageProvider}
                  value={cloudURL}
                  onChange={setCloudURL}
                  onSubmit={handleCloudURLSubmit}
                  isDisabled={!idsValid}
                />
                <StorageProviderInstructions provider={storageProvider} />
              </>
            )}
          </>
        )}

        {isUploading && (
          <Box mt={4}>
            <Progress
              value={isCloudTransfer ? undefined : uploadProgress}
              isIndeterminate={isCloudTransfer}
              colorScheme="purple"
              size="lg"
              hasStripe
              isAnimated
              borderRadius="full"
            />
            <Text mt={2} fontSize="sm" color="muted.400" textAlign="center">
              {isCloudTransfer ? 'Transferring from cloud storage...' : `${uploadProgress}% uploaded`}
            </Text>
          </Box>
        )}
      </Box>
    );
  };

  // ==================== MAIN RENDER ====================

  const showUploadForm = !isAnalyzing && !isComplete;
  const showProgress = isAnalyzing && !isComplete;
  const showResults = isComplete;

  return (
    <Box p={8} textAlign="center" position="relative">
      {/* Page Title */}
      <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color="primary.500" mb={4}>
        VCF to TXT Converter
      </Text>

      {/* Description Box */}
      <Text
        fontSize={{ base: 'md', md: 'lg' }}
        color="primary.500"
        maxW={{ base: '900px', md: '1000px' }}
        mx="auto"
        textAlign={{ base: 'left', md: 'justify' }}
        mb={8}
        lineHeight={1.7}
      >
        Convert VCF (Variant Call Format) files to annotated TXT format for nutrigenomics analysis. 
        This pipeline uses VEP (Variant Effect Predictor) to annotate genetic variants with 
        functional consequences, then generates a comprehensive text report with rsID mappings 
        for downstream analysis in personalized nutrition and wellness applications.
      </Text>

      {/* Upload Form */}
      {showUploadForm && (
        <>
          <InputsSection />

          <Text fontSize="xl" color="muted.400" mt={10} mb={2}>
            Upload your VCF file from local storage or cloud
          </Text>

          <FileUploadSection />

          <Box mt={6} p={4} bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="lg" maxW="700px" mx="auto">
            <Flex align="center" gap={3}>
              <InfoIcon color="blue.500" boxSize={5} />
              <Text fontSize="sm" color="blue.700" fontWeight="medium" textAlign="left">
                This pipeline accepts <strong>VCF</strong> and <strong>VCF.GZ</strong> files. 
                The output will include annotated variants with rsIDs and functional predictions.
              </Text>
            </Flex>
          </Box>

          <Button
            mt={10}
            mb="140px"
            bg="primary.500"
            fontSize="2xl"
            color="white"
            fontWeight="bold"
            px={12}
            py={7}
            borderRadius="full"
            onClick={handleConversion}
            isDisabled={!idsValid || !vcfFileName}
            _hover={{ bg: 'secondary.500', transform: 'scale(1.03)' }}
            _active={{ bg: 'primary.500', transform: 'scale(0.99)' }}
            transition="all 0.18s ease-in-out"
          >
            Convert VCF
          </Button>
        </>
      )}

      {/* Progress View */}
      {showProgress && (
        <Box textAlign="center" p={8}>
          <Text as="h3" fontSize="3xl" mt={10} mx="auto" maxW={{ base: '280px', md: '640px' }} whiteSpace="normal" overflowWrap="break-word" lineHeight="1.3" color="primary.500">
            Converting your VCF – sit tight while we process your data.
          </Text>

          <Text mt={4} color="muted.500" mx="auto" maxW={{ base: '280px', md: '500px' }} fontSize="md" fontStyle="italic">
            This might take a while. We'll email you when your results are ready – feel free to come back later.
          </Text>

          {!!statusDetail && (
            <Text mt={4} color="muted.400" mx="auto" maxW={{ base: '260px', md: '600px' }} whiteSpace="normal" overflowWrap="break-word" fontSize="lg">
              {statusDetail}
            </Text>
          )}

          {progress > 0 && (
            <Text mt={4} mb={2} fontSize="xl" color="primary.500" fontWeight="bold">
              {progress}%
            </Text>
          )}

          <Flex mt={6} justify="center" align="center" maxW="500px" mx="auto">
            <Box position="relative" w="100%" h="24px" bg="surface.300" borderRadius="full" overflow="hidden">
              <Box position="absolute" left={0} top={0} bottom={0} width={`${progress}%`} bg="primary.500" borderRadius="full" transition="width 0.5s ease" />
            </Box>
          </Flex>

          {jobId && (
            <Button mt={8} colorScheme="red" onClick={handleCancelJob} isLoading={isCancelling} loadingText="Cancelling...">
              Cancel
            </Button>
          )}
        </Box>
      )}

      {/* Results View */}
      {showResults && (
        <>
          <Text fontSize="3xl" fontWeight="bold" mt={10} color="primary.500">
            Conversion Complete
          </Text>
          <Text fontSize="lg" color="muted.400" mt={2} maxW="600px" mx="auto">
            Your VCF file has been converted and annotated. Download your results below.
          </Text>

          {/* Download Buttons */}
          <Box mt={10} maxW="600px" mx="auto">
            <Flex mt={4} justify="center" align="center" wrap="wrap" gap={3}>
              {resultFiles.annotated_txt && (
                <>
                  <Button
                    bg="primary.500"
                    color="white"
                    fontWeight="bold"
                    width="200px"
                    py={4}
                    leftIcon={<DownloadIcon />}
                    _hover={{ bg: 'secondary.500' }}
                    onClick={() => {
                      window.open(resultFiles.annotated_txt, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    Download TXT
                  </Button>
                  <Button
                    bg="primary.500"
                    color="white"
                    fontWeight="bold"
                    width="200px"
                    py={4}
                    leftIcon={<ExternalLinkIcon />}
                    _hover={{ bg: 'secondary.500' }}
                    onClick={() => {
                      window.open(resultFiles.annotated_txt_view || resultFiles.annotated_txt, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    View in New Tab
                  </Button>
                </>
              )}
            </Flex>

            <Button
              mt={8}
              mb="140px"
              variant="outline"
              borderColor="primary.500"
              color="primary.500"
              fontWeight="bold"
              px={8}
              py={6}
              borderRadius="full"
              onClick={resetToStart}
              _hover={{ bg: 'surface.100' }}
            >
              Start New Conversion
            </Button>
          </Box>
        </>
      )}

      {/* Chatbot */}
      <Box flexShrink={0}>
        <Chatbot />
      </Box>

      {/* Previous Files Modal */}
      <Modal isOpen={isPreviousFilesOpen} onClose={onClosePreviousFiles} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="700px">
          <ModalHeader>
            <HStack justify="space-between">
              <Text>Select from Previous Uploads</Text>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<RepeatIcon />}
                onClick={fetchPreviousFiles}
                isLoading={isLoadingPreviousFiles}
              >
                Refresh
              </Button>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isLoadingPreviousFiles ? (
              <Center py={10}>
                <Spinner size="xl" color="primary.500" />
              </Center>
            ) : previousFiles.length === 0 ? (
              <Center py={10}>
                <VStack>
                  <Icon as={FiFolder} boxSize={12} color="muted.300" />
                  <Text color="muted.400" fontSize="lg">No previous VCF files found</Text>
                  <Text color="muted.300" fontSize="sm">Upload VCF files to reuse them here</Text>
                </VStack>
              </Center>
            ) : (
              <VStack spacing={2} align="stretch">
                {Object.entries(
                  previousFiles.reduce((acc, file) => {
                    const folder = file.pipeline_folder;
                    if (!acc[folder]) acc[folder] = [];
                    acc[folder].push(file);
                    return acc;
                  }, {} as Record<string, PreviousFile[]>)
                ).map(([folder, files]) => (
                  <Box key={folder}>
                    <Text fontSize="sm" fontWeight="bold" color="muted.500" textTransform="uppercase" mb={2}>
                      {folder.replace(/_/g, ' ')}
                    </Text>
                    <VStack spacing={1} align="stretch">
                      {files.map((file) => (
                        <Flex
                          key={file.key}
                          p={3}
                          bg="surface.50"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="surface.200"
                          cursor="pointer"
                          _hover={{ bg: 'surface.100', borderColor: 'primary.300' }}
                          onClick={() => selectPreviousFile(file)}
                          align="center"
                          justify="space-between"
                        >
                          <HStack spacing={3} flex={1} minW={0}>
                            <Icon as={FiFolder} color="primary.400" />
                            <Text fontWeight="medium" color="text.primary" isTruncated maxW="100%">
                              {file.filename}
                            </Text>
                          </HStack>
                          <HStack spacing={2}>
                            <Badge colorScheme="purple" fontSize="xs">
                              VCF
                            </Badge>
                            <Text fontSize="xs" color="muted.400" whiteSpace="nowrap">
                              {formatFileSize(file.size)}
                            </Text>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                    <Divider my={3} />
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Feedback Modal */}
      {jobId && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          jobId={jobId}
          pipelineType="VCF2TXT"
          onSubmitSuccess={() => setShowFeedbackModal(false)}
        />
      )}
    </Box>
  );
};

export default VCF2TXT;
